import { motion } from "framer-motion";
import {
    Target,
    Zap,
    Users2,
    Compass,
    Briefcase,
    Handshake,
} from "lucide-react";

const philosophy = [
    {
        icon: Users2,
        t: "Businesses Should Scale Beyond Their Founders",
        d: "A company reaches its full potential when success no longer depends on a handful of individuals.",
    },
    {
        icon: Target,
        t: "Operational Excellence Is a Competitive Advantage",
        d: "The companies that win aren't always the ones with the best ideas. They're the ones that execute consistently and efficiently.",
    },
    {
        icon: Compass,
        t: "Every Decision Deserves Context",
        d: "Business intelligence isn't about dashboards. It's about understanding the relationships between people, processes, performance, and outcomes.",
    },
    {
        icon: Zap,
        t: "Complexity Should Never Slow Growth",
        d: "As organizations grow, systems should simplify operations—not create more work.",
    },
    {
        icon: Briefcase,
        t: "Build Once. Improve Continuously.",
        d: "The goal is not to manage more. The goal is to create systems that enable the business to perform better every day.",
    },
    {
        icon: Handshake,
        t: "Alignment Multiplies Performance",
        d: "When every team works from the same priorities and visibility, execution compounds—without adding more meetings or managers.",
    },
];

const milestones = [
    { y: "2023", t: "Founded in Bangalore + Berlin", d: "Three founders, one frustration: fifteen tools, zero coherence." },
    { y: "2024", t: "Closed $14M seed", d: "Backed by Sequoia, Lightspeed and 32 operator angels." },
    { y: "2024", t: "GA launch · v2.0", d: "12,400 teams onboarded in the first 90 days." },
    { y: "2025", t: "ApexBrain Co‑pilot ships", d: "Cross‑module reasoning, plain‑English automations." },
    { y: "2026", t: "14 PoPs · 99.997% uptime", d: "Now powering operators in 84 countries." },
];

const investors = ["Sequoia", "Lightspeed", "Accel", "Index", "Ribbit", "Operator Collective"];

export default function AboutPage() {
    return (
        <div className="pt-28 lg:pt-32" data-testid="about-page">
            {/* Hero */}
            <section className="relative overflow-hidden py-20 lg:py-28">
                <div className="absolute inset-0 grid-bg opacity-[0.25] pointer-events-none" />
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
                    <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                        $ who we are
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
                        <span className="gradient-text">ApexBrain helps founders build businesses that scale beyond individual effort.</span>
                    </h1>
                </div>
            </section>

            {/* Mission + numbers */}
            <section className="relative py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        <div className="lg:col-span-7">
                            <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-4">
                                // mission
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-[1.1]">
                            ApexBrain creates the visibility, accountability, and execution needed for sustainable growth.
                                <br />
                            </h2>
                            <div className="mt-6 space-y-4 text-zinc-300 leading-relaxed font-light text-base">
                                <p>
                                By connecting Sales, People, Projects, Finance, Knowledge, and Communication into one intelligent operating system.
                                </p>
                                <p>
                                Because the most successful companies don't grow through more coordination.
                                </p>
                                <p>
                                They grow through better systems.
                                </p>
                            </div>
                        </div>

                        {/* Stats grid */}
                        <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
                            {[
                                { v: "15", l: "Teams" },
                                // { v: "84", l: "Countries" },
                                // { v: "$32M", l: "Raised" },
                                // { v: "118", l: "Apexians" },
                                { v: "14", l: "Modules" },
                                { v: "99.997%", l: "Uptime" },
                                { v: "14", l: "PoPs" },
                            ].map((s) => (
                                <div
                                    key={s.l}
                                    className="bg-obsidian-200 p-6"
                                >
                                    <div className="font-display text-3xl font-semibold text-white tabular-nums">
                                        {s.v}
                                    </div>
                                    <div className="font-code text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Philosophy */}
            <section className="relative py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-2xl mb-12">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                            // the apexbrain philosophy
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-[1.05]">
                            The ApexBrain Philosophy
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {philosophy.map((v, i) => (
                            <motion.div
                                key={v.t}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="rounded-2xl bg-obsidian-200 border border-white/[0.06] hover:border-white/15 p-7 transition-colors"
                                data-testid={`value-${i}`}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
                                        <v.icon className="w-4 h-4 text-electric" />
                                    </span>
                                    <h3 className="font-display text-lg font-semibold text-white leading-snug">
                                        {v.t}
                                    </h3>
                                </div>
                                <p className="text-sm text-zinc-400 leading-relaxed font-light">
                                    {v.d}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>


       
     
        </div>
    );
}
